import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Navigator from './components/Navigation';
import LifeTodoToggle from './components/LifeTodoToggle';
import { useMetaTags } from './utils/useMetaTags';
import { supabase, DailyEntry } from './lib/supabaseClient';
import './font.css';
import './style.css';

/** "YYYY-MM-DD" → "yy년 mm월 dd일" */
const formatEntryDate = (iso: string): string => {
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [yyyy, mm, dd] = parts;
  return `${yyyy.slice(-2)}년 ${mm}월 ${dd}일`;
};

/** 사용자 로컬 기준 오늘 날짜 (YYYY-MM-DD).
 *  DB의 default current_date는 UTC라 KST 새벽 시간엔 전날로 저장되므로 직접 보낸다. */
const localToday = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** 1~6 점수에 대응되는 라벨. index 0은 미사용. */
const RATING_LABELS: readonly string[] = [
  '',
  '아주나쁨',
  '나쁨',
  '별로',
  '괜찮아',
  '좋음',
  '아주좋음',
];
const ratingLabel = (n: number): string => RATING_LABELS[n] ?? String(n);

/** 1~6 점수에 따른 모달 배경색. index 0은 미사용. */
const RATING_BG_COLORS: readonly string[] = [
  '',
  '#E6DCFF',
  '#DADBFF',
  '#DBF5FD',
  '#E6F5BB',
  '#FFF2C5',
  '#FDDCCD',
];
const ratingBgColor = (n: number): string => RATING_BG_COLORS[n] ?? '#ffffff';

/** 1~6 점수에 따른 슬라이더 핸들(thumb) 색상. index 0은 미사용. */
const RATING_THUMB_COLORS: readonly string[] = [
  '',
  '#2D177C',
  '#4F51E9',
  '#3B9FB9',
  '#569400',
  '#E5BD28',
  '#FC7631',
];
const ratingThumbColor = (n: number): string => RATING_THUMB_COLORS[n] ?? '#131517';

function PageDaily(props: {
  activeNav: number;
  setActiveNav: Dispatch<SetStateAction<number>>;
}) {
  useMetaTags({
    title: 'Daily - Lynkim',
    description: 'Daily',
    image: '/ogimage.png',
  });

  return (
    <div>
      <Navigator activeNav={props.activeNav} setActiveNav={props.setActiveNav} />
      <div className='pageTitle'>Daily</div>
      <DailyBoard />
      <LifeTodoToggle />
    </div>
  );
}

function DailyBoard() {
  const [entries, setEntries] = useState<DailyEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // 가장 많이 등장하는 rating 값 (동률이면 가장 최근에 기록된 것을 우선).
  // entries는 fetch 시 entry_date desc, created_at desc 로 정렬되어 있으므로
  // 동률 중에서는 entries[0] 쪽에 가까운 항목이 더 최신이다.
  const dominantRating = useMemo<number | null>(() => {
    if (entries.length === 0) return null;
    const counts = new Array(7).fill(0) as number[]; // index 1~6 사용
    for (const e of entries) {
      if (e.rating >= 1 && e.rating <= 6) counts[e.rating]++;
    }
    let max = 0;
    for (let r = 1; r <= 6; r++) {
      if (counts[r] > max) max = counts[r];
    }
    if (max === 0) return null;
    // 최신순으로 순회하며 max와 같은 rating이 나오면 그 값을 반환
    for (const e of entries) {
      if (e.rating >= 1 && e.rating <= 6 && counts[e.rating] === max) {
        return e.rating;
      }
    }
    return null;
  }, [entries]);

  // 페이지(body) 배경색을 dominantRating의 모달 배경색으로 적용
  useEffect(() => {
    if (dominantRating == null) return;
    const body = document.body;
    const prev = body.style.backgroundColor;
    body.style.backgroundColor = ratingBgColor(dominantRating);
    return () => {
      body.style.backgroundColor = prev;
    };
  }, [dominantRating]);

  // Daily 토글 active 색을 dominantRating의 핸들 색으로 적용 (CSS 변수로 전달)
  useEffect(() => {
    if (dominantRating == null) return;
    const root = document.documentElement;
    const prev = root.style.getPropertyValue('--daily-active-color');
    root.style.setProperty('--daily-active-color', ratingThumbColor(dominantRating));
    return () => {
      if (prev) root.style.setProperty('--daily-active-color', prev);
      else root.style.removeProperty('--daily-active-color');
    };
  }, [dominantRating]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from('daily_entries')
        .select('*')
        .order('entry_date', { ascending: false })
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (err) setError(err.message);
      setEntries((data ?? []) as DailyEntry[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreate = useCallback(
    async (content: string, rating: number) => {
      const nextPosition =
        entries.length > 0 ? Math.max(...entries.map((e) => e.position)) + 1 : 0;
      const { data, error: err } = await supabase
        .from('daily_entries')
        .insert({ content, rating, position: nextPosition, entry_date: localToday() })
        .select()
        .single();
      if (err) {
        setError(err.message);
        return;
      }
      if (data) {
        setEntries((prev) => [data as DailyEntry, ...prev]);
      }
    },
    [entries]
  );

  const handleContentChange = useCallback((id: string, content: string) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, content } : e)));
  }, []);

  const handleContentCommit = useCallback(async (id: string, content: string) => {
    const { error: err } = await supabase
      .from('daily_entries')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) setError(err.message);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    if (!window.confirm('이 항목을 삭제할까요?')) return;
    setEntries((prev) => prev.filter((e) => e.id !== id));
    const { error: err } = await supabase.from('daily_entries').delete().eq('id', id);
    if (err) setError(err.message);
  }, []);

  if (loading) {
    return <div className='todoBoard' />;
  }

  return (
    <div className='todoBoard'>
      {error && (
        <div
          style={{
            color: 'var(--color-danger)',
            background: 'var(--color-danger-soft)',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <DailyRatingChart entries={entries} />

      <div className='todoGrid'>
        {entries.map((entry) => (
          <DailyCard
            key={entry.id}
            entry={entry}
            onContentChange={(content) => handleContentChange(entry.id, content)}
            onContentCommit={(content) => handleContentCommit(entry.id, content)}
            onDelete={() => handleDelete(entry.id)}
          />
        ))}
      </div>

      <button className='dailyFloatingBtn' onClick={() => setModalOpen(true)}>
        + 기록하기
      </button>

      <NewDailyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={async (text, rating) => {
          await handleCreate(text, rating);
          setModalOpen(false);
        }}
      />
    </div>
  );
}

/** 최근 30일 1~6 점수의 시계열 라인 차트 (페이지 width 100% × 100px 높이) */
function DailyRatingChart({ entries }: { entries: DailyEntry[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // 컨테이너 너비를 ResizeObserver로 추적
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setWidth(el.getBoundingClientRect().width);
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // 오늘로부터 과거 30일치 날짜 (로컬 시간 기준)
  const days = useMemo(() => {
    const fmt = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const arr: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      arr.push(fmt(d));
    }
    return arr;
  }, []);

  // 날짜별 rating (같은 날 여러 기록이면 최신 우선 — entries가 desc 정렬 상태)
  const ratingByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of entries) {
      if (!m.has(e.entry_date)) m.set(e.entry_date, e.rating);
    }
    return m;
  }, [entries]);

  // 점수 없는 날은 기본 4로 간주해서 곡선이 끊기지 않도록 한다.
  const points = useMemo(
    () =>
      days.map((date, i) => ({
        x: i,
        y: ratingByDate.get(date) ?? 4,
        date,
        hasRating: ratingByDate.has(date),
      })),
    [days, ratingByDate]
  );

  const height = 100;
  const padX = 12;
  const padY = 12;
  const innerW = Math.max(width - padX * 2, 0);
  const innerH = height - padY * 2;
  const xAt = (i: number) => padX + (innerW * i) / 29;
  const yAt = (r: number) => padY + (1 - (r - 1) / 5) * innerH;

  // Catmull-Rom → cubic Bezier 변환으로 부드러운 곡선 path 생성
  const path = (() => {
    const pts = points.map((p) => ({ px: xAt(p.x), py: yAt(p.y) }));
    if (pts.length === 0) return '';
    if (pts.length === 1) return `M${pts[0].px.toFixed(1)} ${pts[0].py.toFixed(1)}`;
    let d = `M${pts[0].px.toFixed(1)} ${pts[0].py.toFixed(1)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];
      const cp1x = p1.px + (p2.px - p0.px) / 6;
      const cp1y = p1.py + (p2.py - p0.py) / 6;
      const cp2x = p2.px - (p3.px - p1.px) / 6;
      const cp2y = p2.py - (p3.py - p1.py) / 6;
      d += ` C${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.px.toFixed(1)} ${p2.py.toFixed(1)}`;
    }
    return d;
  })();

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height, marginBottom: 16 }}
      aria-label='최근 30일 점수 추이'
    >
      {width > 0 && (() => {
        const ratedPoints = points.filter((p) => p.hasRating);
        const stroke =
          ratedPoints.length >= 2
            ? 'url(#dailyRatingGradient)'
            : ratedPoints.length === 1
            ? ratingThumbColor(ratedPoints[0].y)
            : 'var(--color-text-primary)';
        const startX = ratedPoints.length >= 2 ? xAt(ratedPoints[0].x) : 0;
        const endX =
          ratedPoints.length >= 2 ? xAt(ratedPoints[ratedPoints.length - 1].x) : 1;
        const range = Math.max(endX - startX, 1);

        return (
          <svg
            width={width}
            height={height}
            style={{
              display: 'block',
              maskImage:
                'linear-gradient(to right, transparent 0%, black 20%, black 95%, transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent 1%, black 20%, black 94%, transparent 99%)',
            }}
          >
            {ratedPoints.length >= 2 && (
              <defs>
                <linearGradient
                  id='dailyRatingGradient'
                  x1={startX}
                  y1={0}
                  x2={endX}
                  y2={0}
                  gradientUnits='userSpaceOnUse'
                >
                  {ratedPoints.map((p) => {
                    const offset = ((xAt(p.x) - startX) / range) * 100;
                    return (
                      <stop
                        key={p.date}
                        offset={`${offset.toFixed(2)}%`}
                        stopColor={ratingThumbColor(p.y)}
                      />
                    );
                  })}
                </linearGradient>
              </defs>
            )}
            {points.length > 1 && (
              <path
                d={path}
                stroke={stroke}
                strokeWidth={3}
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            )}
          </svg>
        );
      })()}
    </div>
  );
}

function NewDailyModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (text: string, rating: number) => Promise<void> | void;
}) {
  const { isOpen, onClose, onSubmit } = props;
  const [text, setText] = useState('');
  const [rating, setRating] = useState<number>(4);
  const [isVisible, setIsVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 모달 마운트/언마운트 트랜지션 — PasswordModal과 동일한 패턴
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setText('');
      setRating(4);
      // 페인트 후 포커스
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setIsVisible(false), 500);
    return () => clearTimeout(t);
  }, [isOpen]);

  if (!isVisible) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed, rating);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={`password-modal-overlay password-modal-overlay--section ${isOpen ? 'modal-open' : 'modal-closing'}`}
      onMouseDown={handleOverlayMouseDown}
      data-theme='light'
    >
      <div
        className={`password-modal-container password-modal-container--section dailyModal ${isOpen ? 'modal-open' : 'modal-closing'}`}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          backgroundColor: ratingBgColor(rating),
          transition:
            'background-color 0.4s ease, opacity 0.8s ease-out, transform 0.5s ease-out',
        }}
      >
        <div className='password-modal-title'>오늘 기록하기</div>
        <form className='password-modal-form' onSubmit={handleSubmit}>
          <div className='dailyRatingField'>
            <div className='dailyRatingValue'>{ratingLabel(rating)}</div>
            <input
              type='range'
              min={1}
              max={6}
              step={1}
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
              className='dailyRatingSlider'
              aria-label='오늘의 점수'
              style={{ ['--thumb-color' as any]: ratingThumbColor(rating) }}
            />
          </div>
          <input
            ref={inputRef}
            type='text'
            className='password-modal-input'
            value={text}
            placeholder='오늘의 한 줄'
            onChange={(e) => setText(e.target.value)}
          />
          <div className='password-modal-buttons'>
            <button
              type='button'
              className='password-modal-button password-modal-button-secondary'
              onClick={onClose}
              disabled={submitting}
            >
              닫기
            </button>
            <button
              type='submit'
              className='password-modal-button password-modal-button-primary dailyKeepBtn'
              disabled={!text.trim() || submitting}
              style={{
                backgroundColor: ratingThumbColor(rating),
                color: '#fff',
                borderColor: 'transparent',
              }}
            >
              남기기
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DailyCard(props: {
  entry: DailyEntry;
  onContentChange: (content: string) => void;
  onContentCommit: (content: string) => void;
  onDelete: () => void;
}) {
  const { entry, onContentChange, onContentCommit, onDelete } = props;
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(
    () => () => {
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    },
    []
  );

  // textarea 높이를 콘텐츠에 맞춰 자동 조정 — 카드 높이가 글자 길이 + padding으로 결정되도록
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // 초기 마운트 및 entry.content 변경 시 높이 재계산
  useEffect(() => {
    autoResize();
  }, [entry.content, autoResize]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    onContentChange(v);
    autoResize();
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => onContentCommit(v), 500);
  };

  return (
    <div
      className='dailyCard fadeInUp'
    >
      <div className='dailyCardHeader'>
        <div className='dailyCardTitleGroup'>
          <span className='dailyCardDate'>{formatEntryDate(entry.entry_date)}</span>
          <span
            className='dailyCardRating'
            style={{ color: ratingThumbColor(entry.rating) }}
          >
            {ratingLabel(entry.rating)}
          </span>
        </div>
        <button
          className='dailyCardDelete'
          onClick={onDelete}
          aria-label='항목 삭제'
        >
          ✕
        </button>
      </div>

      <textarea
        ref={textareaRef}
        className='dailyCardText'
        value={entry.content}
        placeholder='오늘 한 줄'
        onChange={handleChange}
        onBlur={(e) => onContentCommit(e.target.value)}
        rows={1}
      />
    </div>
  );
}

export default PageDaily;
